// Opportunity Detail — region evidence, watch toggle, novelty-probe handoff,
// per mockup screen4-detail.png. Story #4937 Stream 4c.
//
// DOCUMENTED GAPS vs the mockup (same honesty discipline as OpportunityExplorer
// and CombinationMatrix — see their file headers): the mockup shows a
// Bedrock-narrated title, a "why this is open" paragraph, a per-year
// journal-vs-patent chart, an enablement CHECKLIST citing specific document
// sections, drafted claim language, an "Add as concept note" action, and a
// scan-schedule/encroachment-alert panel. None of that is backed by a
// deployed route: whitespace/synthesis.py (which would produce the title,
// narrative, checklist and claim drafts) is not wired into any route yet,
// and there is no per-year time-series endpoint or "concept note" feature
// anywhere in this codebase. This screen renders only what the backend
// actually returns.
//
// Boundary evidence is asymmetric BY NECESSITY, not by choice, and this was
// verified live against the deployed dev backend, not assumed: academic
// record ids resolve to real titles via GET /journals/{id}
// (journals/handler.py:337 does a raw OpenSearch `_doc GET` by `_id` — the
// exact id shape whitespace/handler.py's academic_record_ids carries).
// Patent record ids do NOT resolve through GET /patents/{id} —
// patents/handler.py's own get_patent() docstring says so directly: "the
// index stores docs with `_id = sha(patent_id+source_api)`, so the original
// `client.get(id=patent_id)` always returned 404" — it takes a
// `publication_number`, but whitespace/handler.py's patent_record_ids are the
// index's internal `_id` hash (h['_id'] at whitespace/handler.py:497,549).
// Calling getPatent() with these ids 404s every time (live-confirmed: real
// dev invoke against a real record id from a real scan). Wiring a working
// by-hash lookup is backend work outside this frontend-only task's scope
// (Key Files lists no backend file) — so patent ids are shown as an honest,
// plain record-id list instead of silently swallowing every one as a failed
// fetch, which would look like "no evidence" rather than "can't resolve yet".
import { useQuery } from '@tanstack/react-query'
import { Bookmark, Sparkles, X } from 'lucide-react'
import { getJournal } from '../../services/api'
import {
  formatWhitespaceScore,
  regionLabel,
  regionSummary,
  scoreRingGradient,
} from '../../lib/whitespace'
import type { WhitespaceRegion } from '../../types/whitespace'
import LoadingSkeleton from '../shared/LoadingSkeleton'
import HelpPopover from '../help/HelpPopover'

// design §5.1 "one metric gets one name" — these must match OpportunityExplorer
// v2's card labels exactly (Task #5325), since "Open detail" on that same
// card opens this view: a user must never see "Filing momentum" on the card
// and "Momentum" one click later for the identical number.
const SUB_SCORE_BARS: { key: keyof Pick<WhitespaceRegion, 'sparsity' | 'adjacency' | 'momentum' | 'enablement'>; label: string; color: string; helpTopic: string }[] = [
  { key: 'sparsity', label: 'Openness', color: '#FBBF24', helpTopic: 'ws-sub-sparsity' },
  { key: 'adjacency', label: 'Nearby activity', color: '#0EA5E9', helpTopic: 'ws-sub-adjacency' },
  { key: 'momentum', label: 'Filing momentum', color: '#8B5CF6', helpTopic: 'ws-sub-momentum' },
  { key: 'enablement', label: 'Feasibility signal', color: '#22C55E', helpTopic: 'ws-sub-enablement' },
]

const MAX_EVIDENCE_EACH = 4

interface AcademicEvidenceItem {
  record_id: string
  title: string
  meta: string
}

/** Resolves one academic boundary record id to its real title/journal/date
 * via the existing GET /journals/{id} route. A record id that no longer
 * resolves (deleted, reindexed) is dropped rather than shown as a broken
 * card. Patents are NOT resolved here — see the file header for why. */
async function fetchAcademicEvidence(recordId: string): Promise<AcademicEvidenceItem | null> {
  try {
    const j = await getJournal(recordId)
    return {
      record_id: recordId,
      title: j.title || recordId,
      meta: [j.journal_name, j.publication_date].filter(Boolean).join(' · '),
    }
  } catch {
    return null
  }
}

interface OpportunityDetailProps {
  region: WhitespaceRegion
  isWatched: boolean
  onToggleWatch: (region: WhitespaceRegion) => void
  onRunNoveltyProbe: (region: WhitespaceRegion) => void
  onClose: () => void
}

export default function OpportunityDetail({ region, isWatched, onToggleWatch, onRunNoveltyProbe, onClose }: OpportunityDetailProps) {
  const academicIds = region.academic_record_ids.slice(0, MAX_EVIDENCE_EACH)
  const patentIds = region.patent_record_ids.slice(0, MAX_EVIDENCE_EACH)
  const academicOverflow = Math.max(0, region.academic_record_ids.length - academicIds.length)
  const patentOverflow = Math.max(0, region.patent_record_ids.length - patentIds.length)

  const { data: academicItems, isLoading: academicLoading } = useQuery({
    queryKey: ['whitespace-academic-evidence', region.band[0], region.band[1], academicIds.join(',')],
    queryFn: async () => {
      const resolved = await Promise.all(academicIds.map(fetchAcademicEvidence))
      return resolved.filter((x): x is AcademicEvidenceItem => x !== null)
    },
    enabled: academicIds.length > 0,
  })

  return (
    <div className="light-card p-4" data-testid="opportunity-detail">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 relative"
            style={{ background: scoreRingGradient(region.wos_score) }}
          >
            <div className="w-[38px] h-[38px] rounded-full bg-white flex items-center justify-center">
              <b className="text-sm text-iflow-900">{formatWhitespaceScore(region.wos_score)}</b>
            </div>
          </div>
          <div>
            <div className="text-sm font-bold text-iflow-900">{regionLabel(region)}</div>
            <p className="text-xs text-gray-600 mt-0.5 leading-relaxed max-w-xl">{regionSummary(region)}</p>
          </div>
        </div>
        <button onClick={onClose} aria-label="Close detail" className="text-gray-400 hover:text-gray-600 flex-shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex gap-1.5 flex-wrap mb-4">
        <button
          onClick={() => onToggleWatch(region)}
          aria-pressed={isWatched}
          className={`flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
            isWatched ? 'bg-sky-500/10 border border-sky-300 text-iflow-700' : 'border border-sky-200 text-gray-700 hover:bg-sky-50'
          }`}
        >
          <Bookmark className="w-3 h-3" fill={isWatched ? 'currentColor' : 'none'} />
          {isWatched ? 'Watching' : 'Watch'}
        </button>
        <HelpPopover topic="ws-watch" size={13} label="Help: what watching a region does" />
        <button
          onClick={() => onRunNoveltyProbe(region)}
          className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium bg-amber-400 hover:bg-amber-300 text-gray-900 rounded-md transition-colors"
        >
          <Sparkles className="w-3 h-3" />
          Run novelty probe
        </button>
        <HelpPopover topic="ws-novelty-handoff" size={13} label="Help: what running a novelty probe does" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center gap-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            Region sub-scores
            <HelpPopover topic="ws-how-scored" size={12} label="Help: how the score is built" />
          </div>
          <div className="space-y-1.5">
            {SUB_SCORE_BARS.map(({ key, label, color, helpTopic }) => {
              const value = region[key]
              return (
                <div key={key} className="grid grid-cols-[84px_1fr_24px] gap-1.5 items-center text-[10.5px] text-gray-500">
                  <span className="flex items-center gap-0.5">
                    {label}
                    <HelpPopover topic={helpTopic} size={11} label={`Help: ${label}`} />
                  </span>
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${value}%`, background: color }} />
                  </div>
                  <b className="text-right text-gray-700">{formatWhitespaceScore(value)}</b>
                </div>
              )
            })}
          </div>
          <p className="text-[10.5px] text-gray-500 mt-2 leading-snug">
            Feasibility signal is {formatWhitespaceScore(region.enablement)}/100 — how much of this band's published
            research your project's documents already cover. A section-by-section checklist against your specific
            documents isn't available yet.
          </p>
        </div>

        <div>
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            Boundary prior art — real records at this band's edge
          </div>
          {academicIds.length === 0 && patentIds.length === 0 && (
            <p className="text-xs text-gray-500" data-testid="opportunity-detail-evidence-empty">
              No prior-art records in this band — a genuinely white region.
            </p>
          )}

          {academicIds.length > 0 && (
            <div className="mb-3">
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Journal articles</div>
              {academicLoading && <LoadingSkeleton type="list" rows={2} />}
              {!academicLoading && (academicItems?.length ?? 0) === 0 && (
                <p className="text-xs text-gray-500">Boundary records could not be resolved right now.</p>
              )}
              {!academicLoading && (academicItems?.length ?? 0) > 0 && (
                <ul className="space-y-2" data-testid="opportunity-detail-evidence-list">
                  {academicItems?.map((item) => (
                    <li key={item.record_id} className="text-xs">
                      <span className="inline-block mr-1.5 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-violet-500/10 text-violet-700">
                        Journal
                      </span>
                      <span className="text-gray-800 font-medium">{item.title}</span>
                      {item.meta && <div className="text-[10.5px] text-gray-500 ml-0.5">{item.meta}</div>}
                    </li>
                  ))}
                </ul>
              )}
              {academicOverflow > 0 && (
                <p className="text-[10.5px] text-gray-500 mt-1.5">+{academicOverflow} more journal record{academicOverflow === 1 ? '' : 's'} at this band</p>
              )}
            </div>
          )}

          {patentIds.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
                Patent record ids
              </div>
              <p className="text-[10.5px] text-gray-500 mb-1.5 leading-snug">
                Shown as ids, not titles — GET /patents/{'{id}'} looks records up by publication number, and these
                scan-derived ids are the corpus's internal document ids, so this route can't resolve them yet.
              </p>
              <div className="flex gap-1.5 flex-wrap" data-testid="opportunity-detail-patent-ids">
                {patentIds.map((id) => (
                  <span key={id} className="px-2 py-0.5 text-[10px] font-mono rounded bg-sky-500/10 text-gray-600">
                    {id}
                  </span>
                ))}
              </div>
              {patentOverflow > 0 && (
                <p className="text-[10.5px] text-gray-500 mt-1.5">+{patentOverflow} more patent record{patentOverflow === 1 ? '' : 's'} at this band</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
